import { FC, useEffect, useRef } from 'react';
import { isArray } from 'lodash';

import { useStorage } from '~Components/Storage';
import { useDeviceInfo, useDispatch, useI18n, useLicenses, useLogger, useSelector } from '~Hooks';
import * as actions from '~actions';
import { useLocation } from 'react-router';
import { formatPrinterError } from '~utilities';

interface Props {
  printId: string;
}

const PrintInstance: FC<Props> = ({ printId }) => {
  const dispatch = useDispatch();
  const logger = useLogger('printer');
  const currentRoute = useLocation();
  const deviceInfo = useDeviceInfo();
  const [{ KDSBongOnOrderStatus }] = useStorage();
  const { i18n } = useI18n();

  const licenses = useLicenses();
  const toasts = useSelector((state) => state.toasts);
  const printers = useSelector((state) => state.printers);
  const stations = useSelector((state) => state.stations);
  const print = useSelector((state) => state.POSPrintQueue).find((p) => p.id === printId)!;

  // Initiate first time prints
  useEffect(() => {
    // If we have set a order status to bong on in settings, let the order status universe handle the bonging
    const isHandledInKDSUniverse =
      (licenses.hasKDS || licenses.atleastOneDeviceHasKDS) &&
      !!KDSBongOnOrderStatus &&
      print.printType === 'BONG' &&
      !!print.orderId;

    if (print.isPrinting || isHandledInKDSUniverse) {
      return;
    }

    logger.info('Initiate print in printer queue', {
      data: print,
    });

    dispatch(actions.printQueue(print.id));
  }, []);

  // Logger timeout to prevent both sending "Print failed without error message" and "Print failed" loggers. But we want to send "Print failed without error message" if no error actually comes
  const loggerTimeout = useRef<number | null>(null);

  useEffect(() => {
    if (!print) {
      return;
    }

    // Clear timeout on dependency change
    if (loggerTimeout.current) {
      clearTimeout(loggerTimeout.current);
      loggerTimeout.current = null;
    }

    // Skip if still printing, successful, or BONG type, BONG print error handling is done in another useEffect below
    if (print.isPrinting || print.success || print.printType === 'BONG') {
      return;
    }

    let errorText = formatPrinterError(print.error, i18n);

    // If the failing printer is the primary printer and has kitchen tickets we want to show it very clear that the Receipt failed and the bong print failed so it does not feel spammy with the same error message
    if (print.printType === 'RECEIPT') {
      errorText = i18n('Printer.Error.Receipt') + ': ' + errorText;
    }

    // If there is an immediate print error, handle it without delay
    if (print.error) {
      logger.warn(`Print failed, type: ${print.printType}`, {
        id: print.id,
        print: print,
      });

      const isEpos = ['express', 'self-service-kiosk'].some((path) =>
        currentRoute.pathname.includes(path),
      );

      if (!isEpos) {
        dispatch(actions.addToast(errorText, false, print.id, true));
      }

      return; // Exit early to avoid setting a timeout
    }

    // Delay logging to allow potential errors to surface
    loggerTimeout.current = setTimeout(() => {
      logger.warn(`Print failed without error message, type: ${print.printType}`, {
        id: print.id,
        print: print,
      });

      const isEpos = ['express', 'self-service-kiosk'].some((path) =>
        currentRoute.pathname.includes(path),
      );

      if (!isEpos) {
        let genericErrorText = i18n('Printer.Error.Unknown');
        if (print.printType === 'RECEIPT') {
          genericErrorText = i18n('Printer.Error.Receipt') + ': ' + genericErrorText;
        }

        // If no error message is received, show a generic error message after 8 seconds, 8 seconds is because sometimes it takes time for the printer to respond
        dispatch(actions.addToast(genericErrorText, false, print.id, true));
      }
    }, 8000);

    // Clear timeout on print change
    return () => {
      if (loggerTimeout.current) {
        clearTimeout(loggerTimeout.current);
        loggerTimeout.current = null;
      }
    };
  }, [print.error, print.isPrinting, print.success]);

  // Notify user when bong is successful
  // If this is a reprinted bong, notify user that we successfully reprinted it on primary
  useEffect(() => {
    if (print.isPrinting || !print.success) {
      return;
    }

    if (print.printType === 'BONG') {
      if (print.reprintOfPrintId) {
        dispatch(actions.removeToast(print.reprintOfPrintId));
        dispatch(actions.addToast(i18n('BONG.Failed.BackupPrint'), false, print.id, true));
        logger.warn('Bong print failed. Printed backup bong on primary printer.', {
          id: print.id,
          reprintOfPrintId: print.reprintOfPrintId,
          print: print,
        });
      } else {
        if (!deviceInfo?.isHandheldTerminal) {
          dispatch(actions.addToast(i18n('BONG.sent'), true, print.id));
        }
      }
    }

    dispatch(actions.removePrintFromQueue(print.id));
  }, [print.success]);

  // Notify user when bonging fails
  useEffect(() => {
    if (print.isPrinting || !print.error || print.printType !== 'BONG') {
      return;
    }

    // If a remote bong print fails, try to print it on primary printer and notify user
    const primaryPrinter = printers.find((p) => p.primary);

    // This should not happen on customer facing printers (EPOS/EXPRESS)
    const isEpos = ['express', 'self-service-kiosk'].some((path) =>
      currentRoute.pathname.includes(path),
    );

    if (isEpos) {
      return;
    }

    if (!!primaryPrinter && print.printer !== primaryPrinter.id && !print.reprintedOnPrimary) {
      dispatch(actions.reprintOnPrimary(print.id));

      const printStations =
        printers
          .find((p) => p.id === print.printer)
          ?.stations.map(
            (id) => stations?.find((st) => st.id === id)?.name ?? i18n('BONG.UnknownStation'),
          )
          .join(', ') ?? i18n('BONG.NoStations');

      dispatch(
        actions.addPrintToQueue({
          printer: primaryPrinter.id,
          printerModel: primaryPrinter.model,
          data: isArray(print.data)
            ? [
                ...print.data.slice(0, 7),
                {
                  content: i18n('BONG.Error'),
                  fontSize: 40,
                },
                {
                  content: i18n('BONG.Reprint'),
                  fontSize: 16,
                },
                ...(printStations.length > 0
                  ? [
                      {
                        content: `${i18n('BONG.PrintStations')}: ${printStations}`,
                        fontSize: 16,
                      },
                    ]
                  : []),
                ...print.data.slice(7),
              ]
            : print.data,
          printType: 'BONG',
          reprintOfPrintId: print.id,
          base64Image: print.base64Image,
        }),
      );
      dispatch(actions.addToast(i18n('BONG.Failed.TryingPrimary'), true, print.id, false));
      return;
    }

    // This is a failed backup bong, notify user that this also failed
    if (print.reprintOfPrintId && !toasts.find((t) => t.id === print.id)) {
      dispatch(actions.removeToast(print.reprintOfPrintId));
      dispatch(
        actions.addToast(
          i18n('BONG.Failed.Everywhere') + ' ' + formatPrinterError(print.error, i18n),
          false,
          print.id,
          true,
        ),
      );
      logger.warn('Bong print failed. Also failed to print backup bong on primary printer.', {
        id: print.id,
        reprintOfPrintId: print.reprintOfPrintId,
        printError: print.error,
      });

      return;
    }

    dispatch(
      actions.addToast(
        i18n('BONG.Print.Failed') + ': ' + formatPrinterError(print.error, i18n),
        false,
        print.id,
        true,
      ),
    );
  }, [print.error]);

  return null;
};

export default PrintInstance;
